import React, { FC, ReactNode, useMemo, useRef, useState } from 'react';
import { JobPhaseReceivableSheetInterface } from '@vatos-pas/common';

import { createRowRegistry } from './createRowRegistry';

type EnsureResult = { id: string; created: boolean };

type ReceivableRegistryValue = {
  /** Reactive read: the id created this session for a row, or null. */
  getId: (jobPhaseId: string) => string | null;
  /** Synchronous (non-reactive) read for write-path decisions. */
  peekId: (jobPhaseId: string) => string | null;
  /**
   * Guarantee at most one create per jobPhaseId. Returns the shared id and
   * whether THIS call performed the create. A caller that loses the race gets
   * `created: false` and must apply its own change via a follow-up update.
   */
  ensureCreated: (
    jobPhaseId: string,
    create: () => Promise<string>,
  ) => Promise<EnsureResult>;
};

const {
  Context: ReceivableRegistryContext,
  useRegistry: useReceivableRegistry,
} = createRowRegistry<ReceivableRegistryValue>(
  'useReceivableRegistry',
  'ReceivableRegistryProvider',
);

export { useReceivableRegistry };

/**
 * Row-scoped receivable-id registry, provided once around the receivables list.
 *
 * A row's editable cells (SupplyPro / Billed / Invoice) share a single
 * jobPhaseReceivableId here, so the first edit on a not-yet-created row creates
 * exactly one receivable and the siblings reuse its id. Without the shared id,
 * two cells edited before a refetch would each issue a create, and the
 * backend's unique index on jobPhaseId rejects the second.
 *
 * `getId` is state-backed (reactive, drives re-render so siblings pick up the new
 * id); `peekId`/`ensureCreated` use refs for synchronous correctness on the write
 * path. `record.jobPhaseReceivableId` always takes precedence over the registry,
 * so a natural list refetch transparently supersedes the session-local id.
 */
export const ReceivableRegistryProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [ids, setIds] = useState<Record<string, string>>({});
  const idsRef = useRef<Record<string, string>>({});
  const inFlightRef = useRef<Record<string, Promise<string>>>({});

  const value = useMemo<ReceivableRegistryValue>(() => {
    const getId = (jobPhaseId: string) => ids[jobPhaseId] ?? null;
    const peekId = (jobPhaseId: string) => idsRef.current[jobPhaseId] ?? null;

    const ensureCreated = (
      jobPhaseId: string,
      create: () => Promise<string>,
    ): Promise<EnsureResult> => {
      const known = idsRef.current[jobPhaseId];
      if (known) return Promise.resolve({ id: known, created: false });

      const inFlight = inFlightRef.current[jobPhaseId];
      if (inFlight) return inFlight.then(id => ({ id, created: false }));

      const run = async (): Promise<string> => {
        try {
          const id = await create();
          idsRef.current[jobPhaseId] = id;
          setIds(prev => ({ ...prev, [jobPhaseId]: id }));
          return id;
        } finally {
          delete inFlightRef.current[jobPhaseId];
        }
      };

      const promise = run();
      inFlightRef.current[jobPhaseId] = promise;
      return promise.then(id => ({ id, created: true }));
    };

    return { getId, peekId, ensureCreated };
  }, [ids]);

  return (
    <ReceivableRegistryContext.Provider value={value}>
      {children}
    </ReceivableRegistryContext.Provider>
  );
};

/**
 * Effective receivable id for a row: the record's own id, else the id created
 * this session via the registry (reactive — a sibling-created id propagates
 * without a refetch). `record.jobPhaseReceivableId` always wins. The single
 * source of the id rule, shared by useOptimisticReceivable (write path) and the
 * read-only history cell (which needs only the id, not the commit stack).
 */
export const useReceivableId = (
  record: JobPhaseReceivableSheetInterface | undefined,
): string | null => {
  const registry = useReceivableRegistry();
  const jobPhaseId = record?.jobPhaseId;
  return (
    record?.jobPhaseReceivableId ??
    (jobPhaseId ? registry.getId(jobPhaseId) : null)
  );
};
